import { useRef, useEffect, useMemo } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { quietlight } from '@uiw/codemirror-themes-all';
import { buildPythonEditorExtensions, DEFAULT_EDITOR_SETTINGS } from '../editor/codemirrorConfig';
import { useTheme } from '../context/ThemeContext';

export default function CodeEditor({ value = '', onChange, readOnly = false, height = '400px', editorSettings }) {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const { theme } = useTheme();
  const editorSettingsKey = useMemo(
    () => JSON.stringify({ ...DEFAULT_EDITOR_SETTINGS, ...(editorSettings && typeof editorSettings === 'object' ? editorSettings : {}) }),
    [editorSettings],
  );

  useEffect(() => {
    if (!editorRef.current) return;
    const themeExt = theme === 'dark' ? oneDark : quietlight;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && onChange) {
        onChange(update.state.doc.toString());
      }
    });

    const extensions = [
      ...buildPythonEditorExtensions({
        settings: editorSettings ?? DEFAULT_EDITOR_SETTINGS,
        themeExt,
        height,
        readOnly,
      }),
      updateListener,
    ];

    // readOnly обрабатывается внутри buildPythonEditorExtensions

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [theme, editorSettingsKey, height, readOnly]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (value !== currentDoc) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  return (
    <div className="code-editor-wrapper" ref={editorRef}></div>
  );
}
